import React from "react";
import _isEmpty from "lodash/isEmpty";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ReportPeriodChart from "../../../components/ReportPageComponents/ReportPeriodChart";
import ReportPeriodTable from "../../../components/ReportPageComponents/ReportPeriodTable";
import ReportSpecializationTable from "../../../components/ReportPageComponents/ReportSpecializationTable";
import ReportSpecializationChart from "../../../components/ReportPageComponents/ReportSpecializationChart";
import { PagePeriodFilterContext } from "../../../components/PagePeriodFilter/PagePeriodFilterContextProvider";
import { SectionContainer, SectionTitle } from "../../../components/ReportPageComponents/StyledComponents";
import { DataTypeContext, MONTHLY } from "../../../components/ReportPageComponents/DataTypeSelectionContext";
import { ToolReportResponseSchema, ToolsApi, ToolWIthAdResponse } from "../../../api";
import { ApiQueryKeys } from "../../../api/ApiQueryKeys";
import ReportPage from "../../../components/ReportPageComponents/ReportPage";
import WithLoadingCard from "../../../components/UiKit/WithLoadingCard";
import { getReportPDFExport } from "../../../api/ReportPDFExport";
import { UserContext } from "../../../contexts/UserContext";

const pageType = "tools.tool";

interface ReportI {
  data: ToolReportResponseSchema | undefined;
  dataType: string;
  show: boolean;
}

function Report({ data, dataType, show }: ReportI) {
  const { t } = useTranslation();
  const { user } = React.useContext(UserContext);

  const showOpens = React.useMemo(() => !!user?.permissions?.tool_ad_opens, [user]);
  const keys = React.useMemo(
    () => ["tool_opens", "unique_users", ...(showOpens ? ["tool_ad_opens", "tool_ad_opens_unique_user"] : [])],
    [showOpens]
  );

  if (!(show && data)) {
    return null;
  }
  return (
    <>
      <SectionContainer>
        <SectionTitle>{t(`${pageType}.sectionTitle1`)}</SectionTitle>

        <ReportPeriodChart
          reportType="tools"
          data={dataType === MONTHLY ? data.by_month : data.by_day}
          keys={["tool_opens", "unique_users"]}
          subtitle={showOpens ? t(`${pageType}.chartSubtitle1`) : ""}
        />

        <ReportPeriodChart
          secondaryColors
          hide={!showOpens}
          reportType="tools"
          data={dataType === MONTHLY ? data.by_month : data.by_day}
          keys={["tool_ad_opens", "tool_ad_opens_unique_user"]}
          subtitle={t(`${pageType}.chartSubtitle2`)}
        />

        <ReportPeriodTable
          data={dataType === MONTHLY ? data.by_month : data.by_day}
          total={data.by_day_total}
          keys={keys}
          showOpens={showOpens}
          totalTooltip
        />
      </SectionContainer>

      <SectionContainer>
        <SectionTitle>{t(`${pageType}.sectionTitle2`)}</SectionTitle>
        <ReportSpecializationChart data={data.by_title_and_spec} keyProp="tool_opens" type="top" />
        <ReportSpecializationTable
          data={data.by_title_and_spec}
          keys={["translation", ...keys]}
          type="top"
          defaultSortKey="tool_opens"
        />
      </SectionContainer>

      <SectionContainer>
        <SectionTitle>{t(`${pageType}.sectionTitle3`)}</SectionTitle>
        <ReportSpecializationChart data={data.by_title_and_spec} keyProp="tool_opens" type="rest" />
        <ReportSpecializationTable
          data={data.by_title_and_spec}
          keys={["translation", ...keys]}
          type="rest"
          translationsPrefix="other_users_tooltip."
          defaultSortKey="tool_opens"
        />
      </SectionContainer>
    </>
  );
}

export default function Tool() {
  const { t } = useTranslation();
  const { id } = useParams();
  const { selectedPeriod } = React.useContext(PagePeriodFilterContext);
  const { type: dataType } = React.useContext(DataTypeContext);

  const { data: tools } = useQuery<ToolWIthAdResponse[]>([ApiQueryKeys.toolsStaticData], ToolsApi.toolsStaticData);

  const { refetch, data, isFetching, isError } = useQuery<ToolReportResponseSchema>(
    [ApiQueryKeys.getToolsReport, id, selectedPeriod],
    () =>
      ToolsApi.getToolsReport({
        id: id!,
        dateFrom: selectedPeriod?.from!,
        dateUntil: selectedPeriod?.until!,
      }),
    { enabled: !!id }
  );

  const exportPDF = () => {
    getReportPDFExport({
      type: "tools",
      slug: id!,
      from: selectedPeriod!.from,
      until: selectedPeriod!.until,
      pdf_time_interval: dataType === MONTHLY ? "month" : "day",
    });
  };

  const reportHeaderProps = React.useMemo(() => {
    const selectedTool = tools?.find((to) => to.id === id!);
    const selectedToolAd = selectedTool?.ad;

    return {
      title: selectedTool?.name,
      from: selectedToolAd?.published_from,
      until: selectedToolAd?.published_until,
      image: selectedToolAd?.image,
      showAdDisclaimer: true,
    };
  }, [id, tools]);

  React.useEffect(() => {
    if (selectedPeriod) {
      refetch();
    }
  }, [refetch, selectedPeriod]);

  const showEmptyState = React.useMemo(() => {
    if (isFetching && !data) {
      return false;
    }

    return _isEmpty(data?.by_day);
  }, [data, isFetching]);

  return (
    <ReportPage
      reportHeaderProps={reportHeaderProps}
      showEmptyState={showEmptyState}
      isFetching={isFetching}
      isError={isError}
      exportPDF={exportPDF}
      title={t(`${pageType}.title`)}
    >
      <WithLoadingCard isLoading={isFetching && !data}>
        <Report data={data} dataType={dataType} show={!showEmptyState} />
      </WithLoadingCard>
    </ReportPage>
  );
}
